import { ApolloLink, InMemoryCache, PossibleTypesMap } from '@apollo/client';
import { ErrorResponse } from '@apollo/client/link/error';
import { UseToastOptions, ToastId } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { GraphQLError } from 'graphql';
import _ from 'lodash';
import Cookies from 'universal-cookie';

import { error as commonError } from '@finley-technologies/common';

import { ErrorToastConfig } from 'types/dashboard.d';

import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { TOKEN_NAME } from 'packages/logged-out/hooks/useAuthManagement';

type ToastType = {
  (opts: UseToastOptions): void;
  isActive: (id: ToastId) => boolean | undefined;
};

export type ApolloClientParamTypes = {
  toast: ToastType;
  authToken: string;
  uri: string;
  possibleTypes: PossibleTypesMap;
  removeAuthToken: () => void;
  executionMode: 'live' | 'sandbox';
  targetHeaderValue?: string;
};

const getExternalGqlMessage = (graphQLError: GraphQLError): string | null => {
  return graphQLError?.extensions?.exception?.externalMessage ?? null;
};

type ErrorHandlerResponse = 'forward' | 'terminate';

export const baseGraphQlErrorHandler = (
  toast: ToastType,
  removeAuthToken: () => void,
  errorInputs: {
    graphQLErrors: ErrorResponse['graphQLErrors'];
    operation: ErrorResponse['operation'];
  },
  options: {
    overrideAndSendSentryError: boolean;
  },
): ErrorHandlerResponse => {
  const { graphQLErrors, operation } = errorInputs;
  if (!graphQLErrors || !graphQLErrors[0]) {
    return 'forward';
  }

  const { overrideAndSendSentryError } = options;

  const errMessage =
    getExternalGqlMessage(graphQLErrors[0]) ||
    (graphQLErrors[0].message as string);

  const targetToastConfig: ErrorToastConfig = {
    ...errorToastConfig,
    title: errMessage,
  };
  const headers = operation.getContext().headers;
  // handle expired login separately from errors
  const expiredLogin = graphQLErrors.find((err) => {
    return (
      err.extensions?.code === 'UNAUTHENTICATED' &&
      err.message.includes('Login expired')
    );
  });

  if (expiredLogin) {
    // TODO: consolidate me with useCookie Hook, and manage auth cookie from single scope
    const cookies = new Cookies();
    cookies.remove(TOKEN_NAME, { path: '/' });
    removeAuthToken();
    operation.setContext({
      headers: _.omit(headers, ['authorization']),
    });
    if (!toast.isActive(errorToastId)) {
      toast({ ...targetToastConfig, status: 'warning' });
    }
    // returning undefined means no retry on error
    // https://www.apollographql.com/docs/react/data/error-handling/#on-graphql-errors
    return 'terminate';
  }

  graphQLErrors.forEach((err) => {
    Sentry.addBreadcrumb({
      category: 'apolloClient.graphQLError',
      message: `[Unexpected GraphQL error]: ${err.message}`,
      level: Sentry.Severity.Error,
      data: {
        errLocations: err.locations,
        errPath: err.path,
        errMessage: err.message,
        error: err,
      },
    });
  });

  // We have an unresolved toast error occuring that does
  // not seem to be related to finley systems, suspect heap issue
  // for now, we will (attempt to) silence this error such that
  // customer does not see it.  We will put this early exit after the
  // sentry breadcrumb such that we can continue to monitor / troubleshoot
  if (errMessage.match(/^Unexpected token.*JSON$/)) {
    return 'terminate';
  }

  if (!toast.isActive(errorToastId)) {
    if (overrideAndSendSentryError) {
      targetToastConfig.title =
        'An unexpected error occurred. Please contact Finley for support.';

      Sentry.captureException(
        new commonError.FinleyError(
          `Unmanaged Apollo Server Error, first error: ${errMessage}`,
          { metadata: { logMeta: { graphQLErrors } } },
        ),
      );
    }

    toast({ ...targetToastConfig, status: 'error' });
  }

  // if invalid auth (e.g. invalid JWT), log user out
  const invalidAuth = graphQLErrors.find((err) => {
    return err.extensions?.code === 'UNAUTHENTICATED';
  });
  if (invalidAuth) {
    removeAuthToken();
    operation.setContext({ headers: _.omit(headers, ['authorization']) });
    return 'terminate';
  }

  return 'forward';
};

export const baseNetworkErrorHandler = (
  toast: ToastType,
  networkError: ErrorResponse['networkError'],
): ErrorHandlerResponse => {
  if (!networkError) {
    return 'forward';
  }

  Sentry.addBreadcrumb({
    category: 'apolloClient.networkError',
    message: `[Unexpected Network Error]: ${networkError.message}`,
    level: Sentry.Severity.Error,
    data: {
      errPath: networkError.name,
      errStack: networkError.stack,
      errMessage: networkError.message,
      error: networkError,
    },
  });

  if (!toast.isActive(errorToastId)) {
    toast({ ...errorToastConfig, title: networkError.message });
  }

  return 'forward';
};

export const baseAuthMiddleware = (authToken: string) => {
  return new ApolloLink((operation, forward) => {
    // add the authorization to the headers
    if (authToken) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${authToken}`,
        },
      });
    }
    return forward(operation);
  });
};

export const executionModeMiddleware = ({
  executionMode,
}: {
  executionMode: 'live' | 'sandbox';
}) => {
  return new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    operation.setContext({
      ...context,
      headers: {
        ...context.headers,
        'x-finley-execution-mode': executionMode,
      },
    });
    return forward(operation);
  });
};

export const targetHeaderMiddleware = ({
  targetHeaderValue,
}: {
  targetHeaderValue?: string;
}) => {
  return new ApolloLink((operation, forward) => {
    if (targetHeaderValue) {
      const context = operation.getContext();
      operation.setContext({
        ...context,
        headers: {
          ...context.headers,
          'x-target': targetHeaderValue,
        },
      });
    }
    return forward(operation);
  });
};

// Prevent Apollo Client from displaying arbitrary 500 messages, code taken from github issue:
// https://github.com/apollographql/apollo-feature-requests/issues/153#issuecomment-476832408
export const baseCustomFetch = (uri: string, options: any) => {
  return fetch(uri, options).then((response) => {
    // This is intended only to handle cases of server timeouts that usually come from Cloudfront
    if (response.status > 500) {
      Sentry.captureException(response);
      return Promise.reject(
        new Error(
          'Unexpected server error - please refresh page and retry. Contact support if this persists.',
        ),
      );
    }
    return response;
  });
};

export const baseInMemoryCache = (possibleTypes: PossibleTypesMap) => {
  return new InMemoryCache({
    possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          incompleteDeliverables: {
            merge(_exisiting, incoming) {
              return [...incoming];
            },
          },
          pastDeliverables: {
            merge(_exisiting, incoming) {
              return [...incoming];
            },
          },
        },
      },
      BankDataProviderInstitution: {
        keyFields: ['dataProvider', 'bankInstitutionId'],
      },
      DeliverableItemWithTasks: {
        keyFields: ['deliverableItem', ['id']],
      },
    },
  });
};
